import { OUTREACH_SOURCE_LABELS, OUTREACH_SOURCES } from '@seoplum/core'
import { createFileRoute } from '@tanstack/react-router'
import { Panel } from '@/components/panel'
import { formatExact } from '@/lib/format'
import { useOutreachStats } from '@/lib/outreach'
import { SectionHeader, useSiteScope } from '../../-section'
import { dismissLabel, ProspectTable } from '../-parts'

export const Route = createFileRoute('/_app/$org/$host/outreach/_list/pipeline')({
  component: PipelinePage,
})

/**
 * What the machine is holding: the pool it reads from, in the order it reads
 * it, and what it has been turning down. Nobody works this list; it is here so
 * that an empty drafts page can be told apart from an empty pool.
 */
function PipelinePage() {
  const { org, host, site } = useSiteScope()
  const { data } = useOutreachStats(site)
  const stats = data?.stats

  return (
    <>
      <SectionHeader title="Pipeline" />

      {stats && (
        <div className="grid gap-4 md:grid-cols-2">
          <Panel title="Waiting to be read">
            <dl className="space-y-1.5">
              {OUTREACH_SOURCES.map((source) => (
                <div key={source} className="flex items-baseline justify-between gap-4 text-[13px]">
                  <dt>{OUTREACH_SOURCE_LABELS[source]}</dt>
                  <dd className="font-mono text-[12px] tabular">
                    {formatExact(stats.pool[source])}
                  </dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="Turned down">
            {stats.dismissed.length === 0 ? (
              <p className="font-mono text-[12px] text-muted-foreground">none</p>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr>
                    <th className="pb-1.5 text-left">
                      <span className="sr-only">Reason</span>
                    </th>
                    <th className="pb-1.5 text-right">
                      <span className="mono-label text-muted-foreground">Machine</span>
                    </th>
                    <th className="pb-1.5 text-right">
                      <span className="mono-label text-muted-foreground">You</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.dismissed.map((row) => (
                    <tr key={row.reason}>
                      <td className="py-0.5">{dismissLabel(row.reason)}</td>
                      <td className="py-0.5 text-right font-mono text-[12px] tabular">
                        {row.model}
                      </td>
                      <td className="py-0.5 text-right font-mono text-[12px] tabular">
                        {row.person}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
      )}

      <ProspectTable
        site={site}
        org={org}
        host={host}
        view="pipeline"
        empty={{
          title: 'The pool is empty',
          description: `Nothing is waiting to be read for ${host}.`,
        }}
      />
    </>
  )
}
